<div class="mgr-page">

  <!-- ── Page Header ─────────────────────────────── -->
  <div class="mgr-header">
    <div class="mgr-header__left">
      <div class="mgr-header__icon-wrap">
        <i class="pi pi-chart-bar"></i>
      </div>
      <div>
        <h1 class="mgr-title">Manager View</h1>
        <p class="mgr-subtitle">Monitor workflow cases across pipeline stages.</p>
      </div>
    </div>

    <!-- Case count when board is loaded -->
    <div class="mgr-header__right" *ngIf="columns.length">
      <span class="mgr-summary-chip">
        <i class="pi pi-briefcase"></i>
        {{ totalCases }} Cases
      </span>
      <span class="mgr-summary-chip mgr-summary-chip--cols">
        <i class="pi pi-columns"></i>
        {{ columns.length }} Stages
      </span>
    </div>
  </div>

  <!-- ── Toolbar ─────────────────────────────────── -->
  <div class="mgr-toolbar">
    <div class="mgr-toolbar__field">
      <label class="mgr-toolbar__label">
        <i class="pi pi-sitemap"></i>
        Select Workflow
      </label>

      <div *ngIf="!loadingWorkflows && !workflowError">
        <p-select appendTo="body"
          [options]="workflows"
          [(ngModel)]="selectedWorkflow"
          (onChange)="onWorkflowSelect($event.value)"
          optionLabel="flowName"
          placeholder="Choose an active workflow…"
          [filter]="true"
          filterBy="flowName"
          [showClear]="true"
          styleClass="mgr-workflow-select">

          <ng-template pTemplate="selectedItem" let-item>
            <div class="mgr-select-val" *ngIf="item">
              <i class="pi pi-check-circle mgr-select-val__icon"></i>
              <span>{{ formatFlowName(item.flowName) }}</span>
              <span class="mgr-badge mgr-badge--active">ACTIVE</span>
            </div>
          </ng-template>

          <ng-template pTemplate="item" let-item>
            <div class="mgr-option">
              <span class="mgr-option__icon">
                <i class="pi pi-check-circle"></i>
              </span>
              <div class="mgr-option__body">
                <div class="mgr-option__name">{{ formatFlowName(item.flowName) }}</div>
                <div class="mgr-option__meta">v{{ item.version }} · {{ item.source }}</div>
              </div>
            </div>
          </ng-template>

        </p-select>
      </div>

      <div class="mgr-inline-state" *ngIf="loadingWorkflows">
        <p-progressSpinner strokeWidth="3" styleClass="mgr-spinner"></p-progressSpinner>
        <span>Loading workflows…</span>
      </div>

      <div class="mgr-inline-state mgr-inline-state--error" *ngIf="!loadingWorkflows && workflowError">
        <i class="pi pi-exclamation-circle"></i>
        <span>Failed to load.</span>
        <button class="mgr-btn-retry" (click)="loadWorkflows()">Retry</button>
      </div>
    </div>
  </div>

  <!-- ── Board loading ───────────────────────────── -->
  <div class="mgr-board-loading" *ngIf="loadingBoard">
    <p-progressSpinner strokeWidth="3" styleClass="mgr-spinner"></p-progressSpinner>
    <span>Loading pipeline stages…</span>
  </div>

  <!-- ── Board error ─────────────────────────────── -->
  <div class="mgr-board-error" *ngIf="boardError">
    <i class="pi pi-exclamation-triangle"></i>
    <span>Could not load pipeline for this workflow.</span>
  </div>

  <!-- ── Empty — no workflow selected ───────────── -->
  <div class="mgr-empty" *ngIf="!loadingBoard && !boardError && !columns.length && !selectedWorkflow">
    <div class="mgr-empty__icon"><i class="pi pi-sitemap"></i></div>
    <h3 class="mgr-empty__title">Select a Workflow</h3>
    <p class="mgr-empty__desc">Choose an active workflow above to view its Kanban board.</p>
  </div>

  <!-- ── Kanban Board ────────────────────────────── -->
  <div class="mgr-board" *ngIf="!loadingBoard && columns.length">
    <div class="mgr-col" *ngFor="let col of columns">

      <!-- Column header -->
      <div class="mgr-col__header">
        <div class="mgr-col__header-left">
          <span class="mgr-col__order">{{ col.displayOrder }}</span>
          <span class="mgr-col__name">{{ col.name }}</span>
        </div>
        <span class="mgr-col__count">{{ col.cases.length }}</span>
      </div>

      <!-- Cards -->
      <div class="mgr-col__body">

        <!-- Case card -->
        <div class="mgr-card"
          *ngFor="let card of col.cases"
          (click)="openCase(card)"
          [class.mgr-card--done]="progressPercent(card) === 100"
          [class.mgr-card--in-progress]="progressPercent(card) > 0 && progressPercent(card) < 100">

          <!-- Top: workflow name + percent badge -->
          <div class="mgr-card__head">
            <div class="mgr-card__workflow-icon">
              <i class="pi pi-sitemap"></i>
            </div>
            <div class="mgr-card__workflow-name">{{ card.workflowName }}</div>
            <span class="mgr-card__pct-badge"
              [class.mgr-card__pct-badge--done]="progressPercent(card) === 100"
              [class.mgr-card__pct-badge--active]="progressPercent(card) > 0 && progressPercent(card) < 100">
              {{ progressPercent(card) }}%
            </span>
          </div>

          <!-- Case ID -->
          <div class="mgr-card__id-row">
            <span class="mgr-card__id-chip">
              <i class="pi pi-hashtag"></i>
              {{ card.caseId.slice(-8) }}
            </span>
            <span class="mgr-card__date">
              <i class="pi pi-calendar"></i>
              {{ card.createdAt | appDate:'dayMonth' }}
            </span>
          </div>

          <!-- Progress bar -->
          <div class="mgr-card__progress-wrap" *ngIf="card.totalSop">
            <div class="mgr-card__progress-header">
              <span class="mgr-card__progress-label">
                <i class="pi pi-check-square"></i> Tasks
              </span>
              <span class="mgr-card__progress-count">{{ card.completedSop }}/{{ card.totalSop }}</span>
            </div>
            <div class="mgr-card__progress-bar">
              <div class="mgr-card__progress-fill"
                [style.width.%]="progressPercent(card)"
                [class.mgr-card__progress-fill--done]="progressPercent(card) === 100">
              </div>
            </div>
          </div>

          <!-- Footer: assignee + open arrow -->
          <div class="mgr-card__footer">
            <div class="mgr-card__assignee" *ngIf="card.assignedTo; else unassigned">
              <div class="mgr-card__avatar">{{ card.assignedTo[0]?.toUpperCase() }}</div>
              <span class="mgr-card__assignee-name">{{ card.assignedTo }}</span>
            </div>
            <ng-template #unassigned>
              <div class="mgr-card__assignee mgr-card__assignee--none">
                <div class="mgr-card__avatar mgr-card__avatar--empty"><i class="pi pi-user"></i></div>
                <span class="mgr-card__assignee-name">System</span>
              </div>
            </ng-template>
            <span class="mgr-card__open-hint"><i class="pi pi-arrow-up-right"></i></span>
          </div>

        </div>

        <!-- Empty column -->
        <div class="mgr-col__empty" *ngIf="col.cases.length === 0">
          <i class="pi pi-inbox"></i>
          <span>No cases</span>
        </div>

      </div>
    </div>
  </div>

</div>
